/**
 * CompactVqePopover — anchored, focused visual editor for the cursor statement
 * inside a Query Editor tab. Replaces the always-on inline VQE toolbar with an
 * on-demand popover opened by the pencil edit affordance (gutter or inline).
 *
 * Reuses the SAME edit pipeline as the full VQE: `useInlineToolbarEditContext`
 * wires a per-tab `SqlResolverService` to the Monaco buffer, and the reused
 * `FilterAddBuilder` commits WHERE predicates via `addFilter` → `commitEdit`.
 * The buffer stays the source of truth — no parallel state.
 *
 * Anchoring: a zero-size `PopoverAnchor` is positioned at the statement's first
 * line (same pixel math as `CursorStatementGutterActions`) so the popover opens
 * next to the affordance regardless of the gutter-vs-inline source.
 *
 * When the cursor statement is DDL (`editTarget === 'ddl-card'`), the body
 * swaps to the compact Visual DDL editor (`CompactDdlEditor`), which runs its
 * OWN resolver-backed DDL edit pipeline and degrades to the read-only summary
 * card (`CompactDdlCard`, §2.9 S13, built from a pure local mini-parse of
 * `statementText`) for kinds with no embedded body. The DML edit pipeline
 * (`useInlineToolbarEditContext`) stays deliberately NOT wired to DDL.
 *
 * Analytics: fires `feature:vqe_open_compact` with `{ source }` on open (now
 * also for ddl-card opens — the gate widened, the event shape didn't); the
 * DDL card's own actions fire `feature:vqe_ddl_card_action` with
 * `{ action, objectKind }` on click.
 *
 * spec: docs/superpowers/specs/2026-06-14-vqe-ux-refinement-design.md § WS2+WS3;
 * Plan 9 §2.9/S13 for the DDL card.
 */

import { useEffect, useMemo, useRef } from 'react';

import { Popover, PopoverAnchor, PopoverContent } from '../../../../components/ui/popover';
import { TooltipProvider } from '../../../../components/ui/tooltip';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import { sqlEngineToDatabaseEngineId } from '../../../../core/engines/sqlEngineToDatabaseEngineId';
import { useConnectionsUIStore } from '../../../connections/store/uiStore';
import { useAppStore } from '../../../../store/appStore';
import { isEscapeClaimTarget } from '../../common/utils/escapeClaimTarget';
import type { ParseOptions } from '../../../sql-grammar/model';
import type { EditorSelectionRange } from '../../../../types/aiWorkspace';
import type { SqlMonacoHandle } from '../../common/utils/sqlMonacoTypes';
import type { QueryStructure } from '../../../sql-resolver/types/queryStructure';
import { CompactDdlEditor } from '../../visual-ddl-editor/compact';
import { extractStructureForContext } from '../edit/editContext';
import { useInlineToolbarEditContext } from '../inline/useInlineToolbarEditContext';
import { useWorkspaceStore } from '../../core/store/workspaceStore';
import { useVqeStore } from '../store/vqeStore';
import { useCommitFeedbackToasts } from '../ux/useCommitFeedbackToasts';
import { useEditAppliedStatus } from '../ux/useEditAppliedStatus';
import { useEditHighlight } from '../ux/useEditHighlight';
import { VqeEditStatus } from '../ux/VqeEditStatus';
import { useVqeReadiness } from '../ux/useVqeReadiness';
import type { RenderMode } from '../types';
import { buildCompactSummary } from './compactSummary';
import { buildDdlCardModel, type DdlCardModel } from './compactDdlCard';
import { openDesignTableFromDdlCard } from './openDesignTableFromDdlCard';
import { CompactVqeContent } from './CompactVqeContent';
import { CompactResizeHandles } from './CompactResizeHandles';
import { useCompactDrill, type CompactDrillView } from './useCompactDrill';
import { useResizableBox } from './useResizableBox';
import type { VqeEditAffordanceTarget } from './vqeEditAffordance';

/**
 * `'table-seed'` — opened automatically right after a "Query a table" seed;
 * `'results-grid'` — by a results column header's "Add to Filter".
 */
export type CompactVqeSource = 'gutter' | 'inline' | 'table-seed' | 'results-grid';

const DEFAULT_OPTIONS: ParseOptions = { engine: 'postgres' };

/** Render modes the compact surface can never edit visually → read-only banner. */
const READ_ONLY_MODES: ReadonlySet<RenderMode> = new Set<RenderMode>([
  'unsupported-ddl',
  'unsupported-procedural',
  'unsupported-set-precedence',
  'unsupported-unknown',
  'parse-error',
]);

export interface CompactVqePopoverProps {
  readonly editorRef: React.RefObject<SqlMonacoHandle | null>;
  readonly tabId: string;
  /** Full Monaco buffer text. */
  readonly query: string;
  /** Render mode of the cursor statement (drives the summary verb). */
  readonly mode: RenderMode | null;
  /** Resolver statement id of the cursor statement, or null when unresolved. */
  readonly statementId: string | null;
  /**
   * Source text of the cursor statement — feeds the DDL card's own local
   * mini-parse (`buildDdlCardModel`). Empty string when unresolved; unused
   * unless `editTarget === 'ddl-card'`.
   */
  readonly statementText: string;
  /**
   * Derived edit-affordance target for the cursor statement
   * (`'compact' | 'ddl-card' | null`). `'ddl-card'` renders the DDL summary
   * card instead of the DML edit sections.
   */
  readonly editTarget: VqeEditAffordanceTarget;
  /** Source range of the cursor statement (drives the anchor line). */
  readonly statementRange: EditorSelectionRange | null;
  readonly connectionId?: string | null;
  readonly database?: string | null;
  readonly parseOptions?: ParseOptions;
  /** Whether the popover is open. */
  readonly open: boolean;
  /** Which affordance opened it (analytics + anchor hint). */
  readonly source: CompactVqeSource;
  readonly onOpenChange: (open: boolean) => void;
  /**
   * "Open full editor": show the same statement in the tab's Visual view,
   * keeping the drill the popover is in.
   */
  readonly onOpenFull: (drill: readonly CompactDrillView[]) => void;
}

export function CompactVqePopover({
  editorRef,
  tabId,
  query,
  mode,
  statementId,
  statementText,
  editTarget,
  statementRange,
  connectionId,
  database,
  parseOptions,
  open,
  source,
  onOpenChange,
  onOpenFull,
}: CompactVqePopoverProps) {
  const { track } = useAnalytics();
  // Surface commit-feedback toasts on the compact surface too (§4.2 P12 Task
  // 3) — the hook is multi-mount-safe (see its docblock): a failure while
  // editing here toasts exactly once even if a full-tab workspace for the
  // same buffer is also mounted elsewhere.
  useCommitFeedbackToasts();
  const bannerDismissed = useVqeStore((s) => s.dismissedBanners.has(tabId));
  const dismissBanner = useVqeStore((s) => s.dismissBanner);

  // Local (compact-only) subquery drill stack — NOT the global workspace
  // drillStack. Cleared whenever the popover closes so a re-open starts fresh.
  const drill = useCompactDrill();

  // Drag-to-resize state for the popover box (F1: resizable compact popover).
  // Resets to the CSS default size whenever the popover closes so a re-open
  // starts fresh; the drag-end callback records one analytics event per resize.
  const {
    size: resizeSize,
    boxRef: resizeBoxRef,
    startResize,
    reset: resetResize,
  } = useResizableBox((axis) => track('feature:vqe_compact_resize', { axis }));
  useEffect(() => {
    if (!open) {
      drill.clear();
      resetResize();
    }
  }, [open, drill, resetResize]);

  // DDL summary-card model (§2.9 S13) — built from a pure local mini-parse of
  // the statement text, never from `editContext` (the card commits nothing).
  // `null` for every non-DDL target, so downstream branches stay inert. Also
  // gated on `open` (Task 3 review Minor-1): without it, this memo re-parses
  // on every keystroke while the popover sits CLOSED over a DDL statement —
  // wasted work the popover never renders.
  const ddlCard = useMemo<DdlCardModel | null>(
    () =>
      open && editTarget === 'ddl-card'
        ? buildDdlCardModel(statementText, parseOptions ?? DEFAULT_OPTIONS)
        : null,
    [open, editTarget, statementText, parseOptions],
  );

  // A6/DML-31/DML-34 — sticky-header engine identity badge. `parseOptions`
  // falls back to `DEFAULT_OPTIONS` (postgres) when there is no live
  // connection, so this MUST gate on `connectionId` rather than derive
  // straight from `resolvedEngine` — otherwise a disconnected popover would
  // show a misleading default "PostgreSQL" instead of the badge's own
  // "Not connected" ghost.
  const resolvedEngine = (parseOptions ?? DEFAULT_OPTIONS).engine;
  const engineId = connectionId ? sqlEngineToDatabaseEngineId(resolvedEngine) : undefined;

  // Reuse the inline edit context (same one the old toolbar used). Gated on a
  // live connection inside the hook — without one, the popover renders the
  // disabled Filters control with a one-sentence reason. Also gated off the
  // DDL card: the DML edit pipeline must never wire up to a DDL statement —
  // the card's facts come from `ddlCard` above, not from a resolved scope.
  const editContext = useInlineToolbarEditContext({
    editorRef,
    tabId,
    query,
    statementId: connectionId && !ddlCard ? statementId : null,
    options: parseOptions ?? DEFAULT_OPTIONS,
    connectionId,
    database,
  });

  // "Edit applied · Undo" for this popover's own edits, in its footer. Undo is
  // the Query Editor's own Monaco undo — the same thing Cmd/Ctrl+Z does there.
  const {
    visible: editStatusVisible,
    dismiss: dismissEditStatus,
    editToken,
  } = useEditAppliedStatus({
    documentId: editContext?.documentId ?? null,
    query,
    readSource: () => editorRef.current?.getValue() ?? query,
  });
  // Mark the rows / chips the edit touched while its status shows.
  useEditHighlight({ getRoot: () => resizeBoxRef.current, active: editStatusVisible, editToken });
  const handleEditStatusUndo = () => {
    if (editorRef.current?.undo?.()) {
      track('feature:vqe_undo', { direction: 'undo', via: 'status', surface: 'compact' });
    }
    dismissEditStatus();
  };

  // Header summary: the DDL card supplies its own kind+object summary (its
  // structure isn't DML-shaped, so `buildCompactSummary`/
  // `extractStructureForContext` don't apply); otherwise fall back to the
  // live structure (or the bare verb).
  const summary = useMemo(() => {
    if (ddlCard) {
      return `${ddlCard.kindLabel}${ddlCard.objectName ? ` ${ddlCard.objectName}` : ''}`;
    }
    let structure: QueryStructure | null = null;
    if (editContext) {
      try {
        structure = extractStructureForContext(editContext);
      } catch {
        structure = null;
      }
    }
    return buildCompactSummary(mode, structure);
  }, [ddlCard, editContext, mode]);

  // Anchor at the statement's first line — same gutter pixel math as
  // CursorStatementGutterActions. Recomputed while open + on scroll/layout.
  const anchorRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return undefined;
    const el = anchorRef.current;
    const editor = editorRef.current;
    if (!el || !editor) return undefined;
    const place = () => {
      const line = statementRange?.startLineNumber ?? 1;
      const pos = editor.getPixelPositionForPosition(line, 1);
      if (!pos) return;
      el.style.top = `${pos.top}px`;
    };
    place();
    const offScroll = editor.onDidScrollChange(place);
    const offLayout = editor.onDidLayoutChange(place);
    return () => {
      offScroll();
      offLayout();
    };
  }, [open, editorRef, statementRange]);

  // Fire analytics once per open transition.
  const wasOpenRef = useRef(false);
  useEffect(() => {
    if (open && !wasOpenRef.current) {
      track('feature:vqe_open_compact', { source });
    }
    wasOpenRef.current = open;
  }, [open, source, track]);

  const handleOpenFull = () => {
    onOpenFull(drill.stack);
    onOpenChange(false);
  };

  // "Run on change" — one persisted setting shared with Settings → UI.
  const runOnChangeEnabled = useAppStore((s) => s.visualQueryEditorSettings.runOnChange);
  const setVisualQueryEditorSettings = useAppStore((s) => s.setVisualQueryEditorSettings);
  const runOnChange = useMemo(
    () => ({
      enabled: runOnChangeEnabled,
      onToggle: () => {
        setVisualQueryEditorSettings({ runOnChange: !runOnChangeEnabled });
        track('feature:vqe_run_on_change_toggled', { enabled: !runOnChangeEnabled, source: 'compact' });
      },
    }),
    [runOnChangeEnabled, setVisualQueryEditorSettings, track],
  );

  // DDL card's Edit-as-SQL (decision 5): the popover floats OVER the SQL
  // editor, so "Edit as SQL" here means close + focus the same buffer. The
  // card commits nothing, so there is nothing to reconcile.
  const handleEditAsSql = () => {
    track('feature:vqe_ddl_card_action', {
      action: 'edit-as-sql',
      objectKind: ddlCard?.objectKind ?? 'unknown',
    });
    onOpenChange(false);
    editorRef.current?.focus();
  };

  // The DDL form arms' [Cancel] (pinned next to Edit as SQL): the user backed
  // out, so dismiss the popover. Nothing to roll back — the forms stage into
  // their own local state and only reach the buffer through an applied commit.
  //
  // Deliberately NOT wired to `onDone` (which the bodies also fire after a
  // SUCCESSFUL commit): a commit funnels through the app-shell PreviewDialog,
  // which holds a ctx from `CompactDdlEditor`'s live edit pipeline — tearing
  // the popover down at that moment would pull that context out from under an
  // un-applied preview. Reuses the DDL card's existing analytics event with
  // its own `action` value (no new event name).
  const handleDdlCancel = () => {
    track('feature:vqe_ddl_card_action', {
      action: 'cancel',
      objectKind: ddlCard?.objectKind ?? 'unknown',
    });
    onOpenChange(false);
  };

  // DDL card's Design-table hand-off (decision 3, Task 4): create-mode
  // statements open a Table Designer tab seeded from the statement text;
  // alter-mode statements delegate to the shared `designTableAction`, which
  // reads live metadata. `CompactDdlCard` only renders/enables the button
  // when both a `designTable` seed AND a `connectionId` are present, but the
  // guard here stays defensive against any future caller that skips it.
  const handleDesignTable = () => {
    const design = ddlCard?.designTable;
    if (!design || !connectionId) return;
    track('feature:vqe_ddl_card_action', { action: 'design-table', objectKind: 'table' });
    const result = openDesignTableFromDdlCard({ design, connectionId, database: database ?? null });
    if (result.opened) onOpenChange(false);
  };

  // The EDITABLE DDL body (compact Visual DDL editor). Built only when the
  // cursor statement actually is DDL, so the resolver-backed DDL edit
  // pipeline inside `CompactDdlEditor` is never mounted for SELECT/DML — and
  // never while the popover is closed (`ddlCard` is itself gated on `open`).
  // The editor falls back to the read-only `CompactDdlCard` for kinds with no
  // embedded body yet, so this replaces the card rather than layering on it.
  const ddlBody = ddlCard ? (
    <CompactDdlEditor
      editorRef={editorRef}
      tabId={tabId}
      query={query}
      options={parseOptions ?? DEFAULT_OPTIONS}
      open={open}
      connectionId={connectionId}
      database={database}
      ddlCard={ddlCard}
      onEditAsSql={handleEditAsSql}
      onDesignTable={handleDesignTable}
      onCancel={handleDdlCancel}
    />
  ) : null;

  // Readiness pip state — same derivation as the full-tab workspace banner.
  // The host Query tab's connection alias/host for the header badge.
  const hostConnection = useWorkspaceStore((state) => state.tabs.find((t) => t.id === tabId)?.connectionInfo);

  const readiness = useVqeReadiness({
    connectionId,
    database,
    isReadOnlyMode: mode != null && READ_ONLY_MODES.has(mode),
    service: editContext?.service,
  });
  const bannerState = readiness.state;

  const handleBannerDismiss = () => {
    dismissBanner(tabId);
    track('feature:vqe_readiness_banner_dismiss', { key: 'tab' });
  };

  // § 1.5 — read-only compact relies on the footer's standing "Open full
  // editor" as the single CTA, so the pip drops its own (mismatched: it used
  // to say "Edit as SQL" but opened the full editor). `no-connection` and
  // `snapshot-failed` / `columns-failed` (Reload schema) still route through the pip's CTA.
  const handleBannerPrimaryAction = () => {
    if (bannerState === 'no-connection') {
      useConnectionsUIStore.getState().setShowConnectionsDialog(true);
      return;
    }
    if (bannerState === 'snapshot-failed' || bannerState === 'columns-failed') readiness.reloadSchema();
  };

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverAnchor asChild>
        <div
          ref={anchorRef}
          className="absolute"
          style={{ left: 2, top: 0, width: 1, height: 1 }}
          aria-hidden
        />
      </PopoverAnchor>
      <PopoverContent
        ref={resizeBoxRef}
        align="start"
        side="bottom"
        sideOffset={4}
        // The popover IS the resizable box (F1): `relative` so the absolutely
        // positioned edge/corner handles anchor to it directly (not the Radix
        // Popper wrapper); padding-less + overflow-hidden + flex-col so the
        // inner scroll region + handles pin to its edges. Width defaults to the
        // CSS clamp; once the user drags, an explicit px `style` size takes over
        // and the `max-h` cap is dropped.
        className={`relative flex flex-col overflow-hidden p-0 outline-none w-[min(32rem,90vw)]${
          resizeSize ? '' : ' max-h-[70vh]'
        }`}
        style={resizeSize ? { width: resizeSize.width, height: resizeSize.height } : undefined}
        data-testid="vqe-compact-popover-content"
        data-source={source}
        data-resized={resizeSize ? 'true' : 'false'}
        // Never auto-dismiss on an outside pointer-down or focus shift. The
        // compact popover hosts a multi-step editing surface with nested,
        // body-portalled Radix pickers (add filter / join / table / column /
        // sort) AND sits over the Monaco editor. Both routinely produce
        // "outside" interactions that are really part of editing: a picker's
        // portal is a DOM sibling (not a descendant) of this content, and the
        // Monaco textarea reclaims focus whenever a native <select> dropdown
        // closes. Letting Radix auto-close on those tore the popover (and the
        // open picker) down mid-edit, aborting the in-flight commit. The
        // popover is dismissed only by its explicit affordances — the header
        // ×, Done, Open full editor, Escape, or re-toggling the gutter pencil.
        onPointerDownOutside={(e) => e.preventDefault()}
        onFocusOutside={(e) => e.preventDefault()}
        // Open with focus on the popover itself, not its first control, so
        // nothing shows a focus ring as if it had been pressed. The first Tab
        // reaches the first control; Escape still closes.
        tabIndex={-1}
        onOpenAutoFocus={(e) => {
          e.preventDefault();
          (e.currentTarget as HTMLElement | null)?.focus({ preventScroll: true });
        }}
        // F1 — Escape-in-popover seam consolidation. Radix's DismissableLayer
        // invokes this BEFORE its own `!event.defaultPrevented` dismiss guard
        // (see `escapeClaimTarget.ts`'s docblock for why this beats the old
        // per-input window-capture workaround at the same point in the
        // event's capture-phase dispatch): a VQE-hosted inline editor
        // (`FilterEditablePill`, `LimitValueInline`, `DraftFilterChip` via
        // `VqeFilterChipBuilder`) claims Escape for its own input while
        // editing, so this popover only dismisses when Escape is pressed
        // OUTSIDE any of them.
        onEscapeKeyDown={(e) => {
          if (isEscapeClaimTarget(e.target)) e.preventDefault();
        }}
      >
        {/* Prim #1 §1 — the popover no longer wraps the content in a single
            scroll region. `CompactVqeContent` now owns a THREE-region layout
            (pinned header · scrolling body · pinned footer+preview) and fills
            this `flex-col` box as its `flex-1` child, so the footer stays pinned
            at every content height. Handles sit outside it so they never scroll
            with the content. */}
        <TooltipProvider>
          <CompactVqeContent
            summary={summary}
            mode={mode}
            options={parseOptions ?? DEFAULT_OPTIONS}
            editContext={editContext}
            onOpenFull={handleOpenFull}
            onClose={() => onOpenChange(false)}
            bannerState={bannerState}
            bannerDetail={readiness.failureMessage}
            bannerDismissed={bannerDismissed}
            onBannerDismiss={handleBannerDismiss}
            // read-only: the footer's "Open full editor" owns the CTA; no-database:
            // the pip points to the status bar, where this tab's database is chosen.
            onBannerPrimaryAction={
              bannerState === 'read-only' || bannerState === 'no-database' ? undefined : handleBannerPrimaryAction
            }
            drill={drill}
            tabId={tabId}
            ddlCard={ddlCard}
            ddlBody={ddlBody}
            onEditAsSql={handleEditAsSql}
            connectionId={connectionId}
            onDesignTable={handleDesignTable}
            engineId={engineId}
            database={database ?? undefined}
            connectionName={hostConnection?.connectionName}
            serverHost={hostConnection?.serverHost}
            runOnChange={runOnChange}
            editStatus={<VqeEditStatus visible={editStatusVisible} onUndo={handleEditStatusUndo} />}
          />
        </TooltipProvider>
        <CompactResizeHandles startResize={startResize} />
      </PopoverContent>
    </Popover>
  );
}
